import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  DescriptionOutlined as OffreIcon,
  EditOutlined as EditIcon,
  HandymanOutlined as ServicesIcon,
  HistoryOutlined as HistoryIcon,
  PaymentsOutlined as PayIcon,
  PrintOutlined as PrintIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { auditLogsAPI, comptoirAPI, comptoirPaiementsAPI } from '../../../services/api';
import { useComptoirClientSituation, useInvalidateComptoir } from '../../../hooks/queries/useComptoir';
import { ComptoirOffre } from '../../../types/comptoir';
import ClientFormDialog from '../components/ClientFormDialog';
import EncaisserDialog from '../components/EncaisserDialog';
import OffreWizard from '../components/OffreWizard';
import {
  COLOR, ClientAvatar, ETAT_TONE, Fact, PAIEMENT_TONE, PANEL_SX, Panel, StateChip, dh, digits, fDate, fmtPhone, num, primaryBtnSx,
} from '../ui';

/** Situation client: what the client ordered, paid and still owes, with the relevé. */
const ComptoirClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const cid = Number(id);
  const navigate = useNavigate();
  const { data: d, isLoading, error } = useComptoirClientSituation(cid);
  const invalidate = useInvalidateComptoir();
  const { alertState, showError, showSuccess, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { hasPermission } = usePermissions();
  const canClient = hasPermission('Comptoir', 'edit', 'Clients');
  const canOffre = hasPermission('Comptoir', 'add', 'Offres commerciales');
  const canPay = hasPermission('Comptoir', 'add', 'Services & Projets');
  const canSeeServices = hasPermission('Comptoir', 'view', 'Services & Projets');

  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  if (isLoading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !d) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/comptoir/clients')}>Clients</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error ? extractErrorMessage(error) : 'Client introuvable'}</Alert>
      </Box>
    );
  }

  const c = d.client;
  const t = d.totaux;
  const reste = num(t.reste);
  const openService = (sid: number) => canSeeServices && navigate(`/comptoir/services/${sid}`);

  /* ---------- documents ---------- */
  const printReleve = () => {
    openPreview({
      getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getReleveClientPDFBlob(d),
      fileName: `Releve_${c.nom.replace(/\s+/g, '_')}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirClient', c.id, `Aperçu relevé client - ${c.nom}`);
  };
  const printBon = async (paiementId: number, reference: string) => {
    try {
      const p = await comptoirPaiementsAPI.get(paiementId);
      openPreview({
        getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
        fileName: `Bon_${reference}.pdf`,
      });
      auditLogsAPI.logPdfDownload('ComptoirPaiement', paiementId, `Aperçu bon de paiement - ${reference}`);
    } catch (e: any) { showError(extractErrorMessage(e)); }
  };
  const printDevis = (o: ComptoirOffre) => {
    openPreview({
      getBlob: async () => (await import('../../../utils/comptoirDevisPdfReactPdf')).getComptoirDevisPDFBlob(o),
      fileName: `Devis_${o.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirOffre', o.id, `Aperçu devis - ${o.reference}`);
  };

  // Same digits-only rule as the server (the dialog skips the client being edited)
  const findDuplicate = async (tel: string) => {
    const dg = digits(tel);
    const r = await comptoirAPI.getClients({ search: dg, limit: 20 });
    return r.items.find((x) => digits(x.telephone) === dg) || null;
  };

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/comptoir/clients')} sx={{ mb: 1, color: 'text.secondary' }}>Clients</Button>

      {/* ---------- header ---------- */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <ClientAvatar name={c.nom} size={52} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {c.nom}{c.surnom && <Typography component="span" variant="h5" color="text.secondary"> « {c.surnom} »</Typography>}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fmtPhone(c.telephone)}</Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ lg: 'flex-end' }}
        sx={{ mb: 2, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
        {canClient && <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>Modifier</Button>}
        {canOffre && <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>Nouvelle demande</Button>}
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={printReleve}>Imprimer le relevé</Button>
        {canPay && reste > 0 && <Button variant="contained" startIcon={<PayIcon />} onClick={() => setPayOpen(true)} sx={primaryBtnSx}>Encaisser</Button>}
      </Stack>

      {/* ---------- facts ---------- */}
      <Paper variant="outlined" sx={{ ...PANEL_SX, p: 2 }}>
        <Grid container spacing={2}>
          <Fact label="Téléphone"><span style={{ fontFamily: 'monospace' }}>{fmtPhone(c.telephone)}</span></Fact>
          <Fact label="Surnom">{c.surnom || '—'}</Fact>
          <Fact label="Client depuis">{fDate(c.created_at)}</Fact>
          <Fact label="Demandes">
            {t.services} service(s) / projet(s) · {t.en_cours} en cours · {d.offres.length} offre(s)
          </Fact>
        </Grid>
      </Paper>

      {/* ---------- money ---------- */}
      <Paper variant="outlined" sx={{ ...PANEL_SX, p: 2 }}>
        <Grid container spacing={2}>
          {[
            ['Total des commandes', dh(t.total), 'text.primary'],
            ['Total encaissé', dh(t.paye), 'success.main'],
            [`Solde restant · ${t.non_soldes} demande(s) non soldée(s)`, dh(t.reste), reste > 0 ? 'error.main' : 'success.main'],
          ].map(([k, v, col]) => (
            <Grid item xs={12} sm={4} key={k}>
              <Typography variant="caption" color="text.secondary">{k}</Typography>
              <Typography variant="h5" sx={{ fontFamily: 'monospace', fontWeight: 700, color: col }}>{v}</Typography>
            </Grid>
          ))}
          <Grid item xs={12}>
            <LinearProgress variant="determinate" value={num(t.total) > 0 ? Math.min(100, num(t.paye) / num(t.total) * 100) : 0}
              sx={{ height: 8, borderRadius: 4, bgcolor: alpha(COLOR, 0.12), '& .MuiLinearProgress-bar': { bgcolor: reste <= 0 ? 'success.main' : COLOR } }} />
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- services ---------- */}
      <Panel title="Services et projets" icon={<ServicesIcon />} count={d.services.length}>
        {d.services.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucun service pour ce client.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Réf.</TableCell><TableCell>Date</TableCell><TableCell>Travaux</TableCell><TableCell>Livraison</TableCell>
                  <TableCell align="right">Total</TableCell><TableCell align="right">Payé</TableCell><TableCell align="right">Reste</TableCell>
                  <TableCell>État</TableCell><TableCell>Paiement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.services.map((x) => (
                  <TableRow key={x.id} hover={canSeeServices} onClick={() => openService(x.id)} sx={{ cursor: canSeeServices ? 'pointer' : 'default' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{x.reference}</Typography>
                      <Typography variant="caption" color="text.secondary">{x.nature}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(x.accepte_le)}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap title={x.titre}>{x.titre}</Typography>
                      <Typography variant="caption" color="text.secondary">{x.nb_travaux} travail(x)</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {x.derniere_livraison
                        ? <>{fDate(x.derniere_livraison)}{x.etat === 'livre_partiel' && <Chip label="partielle" size="small" color="warning" variant="outlined" sx={{ ml: 0.75, height: 20 }} />}</>
                        : <Typography variant="caption" color="text.secondary">{x.deadline ? `prévue ${fDate(x.deadline)}` : '—'}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{dh(x.total)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'success.main' }}>{dh(x.paye)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', fontWeight: 700, color: num(x.reste) > 0 ? 'error.main' : 'text.secondary' }}>{dh(x.reste)}</TableCell>
                    <TableCell><StateChip small label={x.etat_com_label || x.etat_label} t={ETAT_TONE[x.etat_com || x.etat]} /></TableCell>
                    <TableCell><StateChip small label={x.paiement_label} t={PAIEMENT_TONE[x.paiement]} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Panel>

      <Grid container spacing={2.5}>
        {/* ---------- payments ---------- */}
        <Grid item xs={12} md={7}>
          <Panel title="Historique financier" icon={<PayIcon />} count={`${d.paiements.length} paiement(s)`}>
            {d.paiements.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucun paiement enregistré.</Typography>
            ) : d.paiements.map((p) => (
              <Stack key={p.id} direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{p.reference}</Box>
                    {' · '}{fDate(p.date)} · {p.mode}{p.reference_paiement ? ` · ${p.reference_paiement}` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {p.allocations.map((a) => `${a.service_reference} ${a.type} · ${dh(a.montant)}${a.annule ? ' (annulé)' : ''}`).join(' — ')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{dh(p.montant)}</Typography>
                <Tooltip title="Imprimer le bon de paiement">
                  <IconButton size="small" onClick={() => printBon(p.id, p.reference)} sx={{ color: COLOR }}><PrintIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Panel>
        </Grid>

        {/* ---------- offres ---------- */}
        <Grid item xs={12} md={5}>
          <Panel title="Offres commerciales" icon={<OffreIcon />} count={d.offres.length}>
            {d.offres.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucune offre.</Typography>
            ) : d.offres.map((o) => (
              <Stack key={o.id} direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={o.titre}>
                    <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{o.reference}</Box> · {o.titre}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{fDate(o.date)} · {dh(o.total)}</Typography>
                </Box>
                {o.service && (
                  <Chip size="small" variant="outlined" label={o.service.reference} clickable={canSeeServices}
                    onClick={canSeeServices ? () => navigate(`/comptoir/services/${o.service!.id}`) : undefined}
                    sx={{ fontFamily: 'monospace', fontWeight: 700, borderColor: alpha(COLOR, 0.5), color: COLOR }} />
                )}
                <Tooltip title="Imprimer le devis">
                  <IconButton size="small" onClick={() => printDevis(o)} sx={{ color: COLOR }} aria-label={`Imprimer le devis ${o.reference}`}><PrintIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Panel>
        </Grid>
      </Grid>

      {/* ---------- history ---------- */}
      <Panel title="Historique" icon={<HistoryIcon />} count={d.historique.length}>
        {d.historique.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucun événement.</Typography> : d.historique.map((h) => (
          <Stack key={h.id} direction="row" spacing={2} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>{new Date(h.date).toLocaleString('fr-FR')}</Typography>
            <Box>
              <Typography variant="body2">
                {h.reference && <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, mr: 1 }}>{h.reference}</Box>}
                {h.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">{h.user_name || '—'}</Typography>
            </Box>
          </Stack>
        ))}
      </Panel>

      {/* ---------- dialogs ---------- */}
      <ClientFormDialog
        open={editOpen} client={c} onClose={() => setEditOpen(false)}
        save={(data) => comptoirAPI.updateClient(c.id, data)}
        findDuplicate={findDuplicate}
        onSaved={() => { setEditOpen(false); invalidate(); showSuccess('Fiche client enregistrée'); }}
      />
      <EncaisserDialog
        open={payOpen} clientId={c.id} clientNom={c.nom}
        onClose={() => setPayOpen(false)}
        onSaved={(p) => {
          setPayOpen(false);
          invalidate();
          openPreview({
            getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
            fileName: `Bon_${p.reference}.pdf`,
          });
        }}
      />
      <OffreWizard
        open={wizardOpen} offre={null} presetClientId={c.id} onClose={() => setWizardOpen(false)}
        onSaved={(o, _isNew, print) => {
          setWizardOpen(false);
          invalidate();
          if (print) printDevis(o);
          else showSuccess(`Offre ${o.reference} créée`);
        }}
      />
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
      {pdfPreviewElement}
    </Box>
  );
};

export default ComptoirClientDetail;
